import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import imgArrowLeft from '../../images/common/ic_arrow_left.svg';
import imgArrowRight from '../../images/common/ic_arrow_right.svg';
import imgArrowDLeft from '../../images/common/ic_arrow_double_left.svg';
import imgArrowDRight from '../../images/common/ic_arrow_double_right.svg';
import ColorSF2 from '../../styles/common/ColorSF2';

const Main = styled.div`
  flex: 0 0 auto;
  height: 50px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${ColorSF2.paleBlue};
  padding: 0 10px;
  @media (max-width: 830px), (max-height: 530px) {
    height: 30px;
  }
`;
const PageRight = styled.div`
  min-width: 60px;
  color: ${ColorSF2.black};
  text-align: right;
`;
const PaginationWrapper = styled.div`
  display: flex;
  margin: 0 10px;
  height: 26px;
`;
const ImageAngle = styled.img`
  height: 100%;
  display: flex;
  justify-content: center;
`;
const PageNext = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
`;
const PagePrev = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
`;
const PageBlock = styled.div`
  margin-left: ${props => (props.current ? '0px' : '1px')};
  width: 38px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  cursor: ${props => (props.current ? 'default' : 'pointer')};
  background-color: ${props =>
    props.current ? `${ColorSF2.skyBlue}` : `${ColorSF2.paleBlue}`};
  color: ${ColorSF2.black};
`;

const TOP_PAGE = 1;
const DISPLAY_PAGE = 7;
const DISPLAY_CENTER = 3;

/**
 * ページネーションコンポーネントを生成する。
 * @param {number} totalPages - 全ページ数。
 * @param {number} currentPage - 現在のページ。
 * @param {function} onSelect - ページ番号が選択されたときのコールバック関数。
 * @returns {React.Component} - 生成されたページネーションコンポーネント。
 */
const createPagination = (totalPages, currentPage, onSelect) => {
  const prevPage = currentPage - 1 > 0 ? currentPage - 1 : currentPage;
  const nextPage =
    currentPage + 1 <= totalPages ? currentPage + 1 : currentPage;
  let firstPage =
    currentPage > DISPLAY_PAGE - 1 ? currentPage - DISPLAY_CENTER : TOP_PAGE;
  let lastPage =
    currentPage > DISPLAY_PAGE - 1
      ? currentPage + DISPLAY_CENTER
      : DISPLAY_PAGE;
  if (lastPage > totalPages) {
    lastPage = totalPages;
    firstPage = totalPages - (DISPLAY_PAGE - 1);
  }
  if (firstPage < TOP_PAGE) {
    firstPage = TOP_PAGE;
  }
  const pageCount = lastPage - firstPage + 1;
  return (
    <PaginationWrapper>
      <PagePrev
        className="paginationClickable"
        id="timelinePagenationMoveToStartButton"
        role="presentation"
        key="moveToStartButton"
        onClick={() => onSelect(1)}
      >
        <ImageAngle src={imgArrowDLeft} alt="" />
      </PagePrev>
      <PagePrev
        className="paginationClickable"
        id="timelinePagenationPrevButton"
        role="presentation"
        key="prevButton"
        onClick={() => onSelect(prevPage)}
      >
        <ImageAngle src={imgArrowLeft} alt="" />
      </PagePrev>
      <PaginationWrapper>
        {[...Array(pageCount)].map((_, i) => {
          const count = i + firstPage;
          const clickableClassName =
            count !== currentPage ? 'paginationClickable' : '';
          return (
            <PageBlock
              current={count === currentPage}
              className={clickableClassName}
              id={`timelinePaginationCount${i}`}
              role="presentation"
              key={`${count}`}
              onClick={() => onSelect(count)}
            >
              <div>{count}</div>
            </PageBlock>
          );
        })}
      </PaginationWrapper>
      <PageNext
        className="paginationClickable"
        id="timelinePagenationNextButton"
        role="presentation"
        key="nextButton"
        onClick={() => onSelect(nextPage)}
      >
        <ImageAngle src={imgArrowRight} alt="" />
      </PageNext>
      <PageNext
        className="paginationClickable"
        id="timelinePagenationMoveToEndButton"
        role="presentation"
        key="moveToEndButton"
        onClick={() => onSelect(totalPages)}
      >
        <ImageAngle src={imgArrowDRight} alt="" />
      </PageNext>
    </PaginationWrapper>
  );
};

/**
 * ページネーションおよび合計件数を表示するコンポーネント。
 * @param {Object} props
 * @param {string} props.id - コンポーネントのID。デフォルトは'pagination'。
 * @param {number} props.totalNumber - 全件数。
 * @param {number} props.totalPages - 全ページ数。
 * @param {number} props.currentPage - 現在のページ。
 * @param {function} props.onSelect - ページ番号が選択されたときのコールバック関数。
 * @returns {React.Component} - ページネーションおよび合計件数表示コンポーネント。
 * @example
 *        <Pagination
            totalNumber={totalNumber}
            totalPages={totalPages}
            currentPage={currentPage}
            onSelect={page => dispatch({ type: 'SELECT_BLACKBOARD_PAGE', page })}
          />
 */
const Pagination = ({ id, totalNumber, totalPages, currentPage, onSelect }) => (
  <Main id={id}>
    <div />
    {(totalPages > 1 &&
      createPagination(totalPages, currentPage, onSelect)) || <div />}
    <PageRight>
      {totalNumber}
      <FormattedMessage id="timeline.pagenation.unit" />
    </PageRight>
  </Main>
);

Pagination.propTypes = {
  id: PropTypes.string,
  totalNumber: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
};

Pagination.defaultProps = {
  id: 'pagination',
};

export default Pagination;
